<template>
  <div class="article-detail">
    <Breadcrumbs :breadcrumbs="breadcrumbs" class="mt-4 hidden md:block" />

    <!-- Main content area with responsive layout -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
      <!-- Main content column -->
      <div class="col-span-3" :class="{ 'lg:col-span-2': !articlesCategory }">
        <div class="w-full">
          <MagazineArticleHeader
            v-if="articleObject"
            :article="articleObject"
          />

          <div v-if="articlesCategory">
            <h1 class="text-h4 md:text-[26px] font-medium">
              {{ articleCategoryGetters.getTitle(articlesCategory) }}
            </h1>
            <p class="text-h5 md:text-xl font-light md:font-medium my-4 prose">
              {{ articleCategoryGetters.getDescription(articlesCategory) }}
            </p>
            <hr class="my-3 border-gray-600 md:hidden" />
          </div>

          <MagazineArticleSidebar
            class="md:hidden"
            :parsed-article-content="parsedArticleContent"
            :articles-category="articlesCategory"
          />

          <div
            v-if="
              articleObject?.summary_title || articleObject?.summary_content
            "
            class="summary-block"
          >
            <div
              v-if="articleObject?.summary_title"
              class="summary-block__title h4"
            >
              {{ articleObject?.summary_title }}
            </div>
            <vue-markdown
              v-if="articleObject?.summary_content"
              class="prose summary-block__text"
              :source="getFormattedContent(articleObject?.summary_content)"
            />
          </div>

          <MagazineArticleContent
            v-if="articleObject"
            :parsed-article-components="parsedArticleComponents"
          />

          <MagazineArticleAuthor
            v-if="articleObject?.author_information?.author_description"
            v-bind="articleObject?.author_information"
            :partner-image="supplierGetters.getLogo(partnerData)"
            :partner-link="
              supplierGetters.getPermalink(partnerData)
                ? supplierGetters.getPermalink(partnerData)
                : ''
            "
          />

          <!--     NEED TO PARSE HERE     -->
          <Faq v-if="faq" :content="[faq]" class="article-faq" />

          <div v-if="articleObject" class="my-10" />

          <div v-if="articlesCategory" class="flex flex-wrap justify-between">
            <MagazineArticleCard
              v-for="article in [firstArticleCluster, ...articleClusterItems]"
              :key="article.link"
              class="md:w-[calc(50%-1rem)] gap-[1rem] py-4"
              v-bind="article"
            />
          </div>
        </div>
      </div>

      <!-- Sidebar column -->
      <div class="lg:col-span-1 hidden lg:block">
        <MagazineArticleSidebar
          :parsed-article-content="parsedArticleContent"
          :articles-category="articlesCategory"
        />
      </div>
    </div>

    <div v-if="articleObject">
      <NuxtLazyHydrate when-visible>
        <MagazineArticlePartnerEvents />
      </NuxtLazyHydrate>

      <NuxtLazyHydrate when-visible>
        <MagazineArticleRelatedArticles data-test-id="related-articles" />
      </NuxtLazyHydrate>
    </div>
  </div>
</template>

<script setup lang="ts">
import VueMarkdown from 'vue-markdown-render'
import { useNotificationsStore } from '../../../stores/notifications'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type {
  EventDescription,
  GalleryItem,
  Supplier,
} from '~/composables/types/event'
import { useMagazineArticleSchema } from '~/composables/useMagazineArticleSchema'
import { replacePlaceholders } from '~/utils/dynamicContentTransformer'
import type {
  StrapiMagazineArticle,
  StrapiMagazineArticleCategory,
} from '~/composables/types/strapi/strapi-magazine'
import { useResolvedMagazinePage } from '~/composables/magazine/useResolvedMagazinePage'

const $route = useRoute()
const $localePath = useLocalePath()
const localePath = $localePath
const { locale, t, te } = useI18n()
const { addNotification } = useNotificationsStore()
const { parseMarkdownWithBlock } = useKftArticleParser()

const articleSlug = $route.params.articleSlug?.toString()
const deleteAlertNotification = ref(undefined)

const { status, data: magazinePageData } =
  await useResolvedMagazinePage(articleSlug)

const articleObject = computed<StrapiMagazineArticle | null>(() => {
  const d = magazinePageData.value
  return d && 'content' in d ? (d as StrapiMagazineArticle) : null
})

const articlesCategory = computed<StrapiMagazineArticleCategory | null>(() => {
  const d = magazinePageData.value
  return d && !('content' in d) ? (d as StrapiMagazineArticleCategory) : null
})

provide('article', articleObject)

const articleMetrics = computed(() =>
  articleGetters.getArticleMetrics(articleObject.value)
)

const getDynamicContent = async () => {
  if (!articleMetrics?.value?.entity || !articleMetrics?.value?.value) return

  const { entity, value } = articleMetrics.value
  const { data: dynamicContent } = await useKftApiFetch(
    `/v1/store/metrics/${entity}/${value}`
  )

  return dynamicContent
}

const articleMetricContent = await getDynamicContent()

type getFormattedContentType = (content: string | null) => string
const getFormattedContent: getFormattedContentType = (content) => {
  if (!content) return

  if (!articleMetricContent?.value) {
    return content
  }

  return replacePlaceholders(
    `${content}`,
    articleMetricContent.value.data,
    locale.value
  )
}

const articleContent = computed(() => {
  const content = articleGetters.getContent(articleObject?.value)
  return getFormattedContent(content)
})

const parsedArticleComponents = computed(() =>
  articleContent.value ? parseMarkdownWithBlock(articleContent.value) : []
)

const partnerId = computed(() =>
  articleGetters.getSupplierId(articleObject.value)
)

// Only throw 404 on server; on client hydration async data may not be restored yet
if (
  import.meta.server &&
  status.value === 'success' &&
  !magazinePageData.value
) {
  throw showError({
    statusCode: 404,
    message: 'Page Not Found',
  })
}

const { data: partnerData } = partnerId.value
  ? await useKftApiFetch<KftApiDefaultResponse<Supplier>, Supplier>(
      `v1/store/supplier/${partnerId.value}?include=logo,headerLogo`,
      {
        transform: (input) => input?.data || undefined,
      }
    )
  : { data: ref(undefined) }

provide('partnerData', partnerData)

const { data: partnerEvents } = partnerId.value
  ? await useKftApiFetch<KftApiDefaultResponse<EventDescription[]>>(
      `v1/store/events/?limit=50&search=supplier_id:${partnerId.value};konfettiPublished:1;hasEvent:false`,
      {
        transform: (res: KftApiDefaultResponse<EventDescription[]>) => {
          res.data.forEach((event) => {
            // delete videos from media and photos to avoid crawlers trying to index
            // urls that dot not work
            if (event?.media) {
              event.media = event.media.filter(
                (media: GalleryItem) => media.media_type !== 'video'
              )
            }

            if (event?.media) {
              event.media = event.media.filter(
                (photo: GalleryItem) => photo.media_type !== 'video'
              )
            }
          })

          return res
        },
      }
    )
  : { data: ref(undefined) }

provide('partnerEvents', partnerEvents)

watch(
  () => partnerId.value,
  async () => {
    const partnerDataResponse = await kftApiRequest<
      KftApiDefaultResponse<Supplier>
    >(`v1/store/supplier/${partnerId.value}?include=logo,headerLogo`, {
      method: 'GET',
    })
    const partnerEventsResponse = await kftApiRequest<
      KftApiDefaultResponse<EventDescription[]>
    >(
      `v1/store/events/?limit=50&search=supplier_id:${partnerId.value};konfettiPublished:1;hasEvent:false`,
      {
        method: 'GET',
      }
    )
    partnerData.value = partnerDataResponse?.data
    partnerEvents.value = partnerEventsResponse?.data
  }
)

const parsedArticleContent = computed(() => {
  let resultStr = ''
  parsedArticleComponents.value
    .filter((x) => !x.componentName)
    .forEach((x) => {
      resultStr = resultStr.concat(x.content)
    })

  return resultStr
})

const breadcrumbs = computed(() => [
  {
    text: t('general.home'),
    link: localePath('/'),
  },
  {
    text: t('magazine.magazine'),
    link: localePath('/magazine'),
  },
  ...(articleGetters.getFirstCategory(articleObject.value) !== null
    ? [
        {
          text: articleCategoryGetters.getTitle(
            articleGetters.getFirstCategory(articleObject.value)
          ),
          link: localePath(
            `/magazine/${articleCategoryGetters.getSlug(
              articleGetters.getFirstCategory(articleObject.value)
            )}`
          ),
        },
      ]
    : []),
  {
    text:
      articleGetters.getTitle(articleObject.value) ??
      articleCategoryGetters.getTitle(articlesCategory.value),
    link: localePath(
      `/magazine/${
        articleGetters.getSlug(articleObject.value) ??
        articleCategoryGetters.getSlug(articlesCategory.value)
      }`
    ),
  },
])
const alertMessageMarkdown = computed(() => {
  const message = articleGetters.getAlertText(articleObject.value)

  return getFormattedContent(message)
})

const addAlertMessage = async () => {
  const alertMessageHtml =
    alertMessageMarkdown.value && kftMicroDown.parse(alertMessageMarkdown.value)

  if (!alertMessageHtml) {
    return
  }

  deleteAlertNotification.value = await addNotification(
    {
      htmlContent: alertMessageHtml,
      type: 'SUCCESS',
    },
    { shouldScrollToTop: false }
  )
}

onMounted(async () => {
  setTimeout(() => {
    window?.scrollTo({ top: 0, behavior: 'smooth' })
  }, 100)

  await addAlertMessage()
})

onBeforeUnmount(() => {
  if (deleteAlertNotification.value) {
    deleteAlertNotification.value()
  }
})

const createArticleClusterItem = (article: StrapiMagazineArticle) => ({
  author: articleGetters.getAuthor(article),
  date: formatDate(
    articleGetters.getUpdatedAt(article),
    locale.value,
    'dd. DD. MMM YYYY'
  ),
  image: articleGetters.getHeaderImageUrl(article, 'thumbnail'),
  title: articleGetters.getTitle(article),
  description: articleGetters.getDescription(article),
  link: localePath(`/magazine/${articleGetters.getSlug(article)}`),
})

const articleClusterItems = computed(() => {
  const articles = articleCategoryGetters.getArticles(articlesCategory.value)

  return (
    articles?.slice(1).map((article) => createArticleClusterItem(article)) || []
  )
})

const firstArticleCluster = computed(() => {
  const articles = articleCategoryGetters.getArticles(articlesCategory.value)

  if (!articles || articles.length === 0) {
    return undefined
  }

  const article = articles?.[0]
  const obj = createArticleClusterItem(article)

  return obj
})

const faq = computed(() => {
  const faqItems = articleGetters.getFaq(articleObject.value)

  if (!faqItems) return

  return {
    ...faqItems,
    questions: faqItems?.questions?.map((item) => {
      return {
        ...item,
        question: getFormattedContent(item.question),
        answer: getFormattedContent(item.answer),
      }
    }),
  }
})

const headerImageUrl = articleGetters.getHeaderImageUrl(
  articleObject.value,
  'small'
)
const seoImageUrl = articleGetters.getSeoImageUrl(articleObject.value, 'small')

const { script: magazineArticleSchema } = useMagazineArticleSchema(
  articleObject.value,
  partnerData.value
)

const getFormattedDescription = computed(() => {
  const description =
    articleGetters.getSeoMetaDescription(articleObject.value) ||
    articleCategoryGetters.getSeoMetaDescription(articlesCategory.value) ||
    t('general.descriptions.magazine-slug', {
      description:
        articleGetters.getDescription(articleObject.value) ||
        articleCategoryGetters.getDescription(articlesCategory.value),
    })

  return getFormattedContent(description)
})

watch(
  () => alertMessageMarkdown.value,
  async () => await addAlertMessage()
)

const magazineHreflangLinks = useMagazineHreflang(
  articleObject,
  articlesCategory
)

const seoOptions = computed(() => ({
  allowedParams: [],
  shouldBeIndexed: true,
  link: magazineHreflangLinks.value,
  meta: {
    title:
      articleGetters.getSeoTitle(articleObject.value) ||
      articleCategoryGetters.getSeoTitle(articlesCategory.value) ||
      t('general.titles.magazine-slug', {
        article:
          articleGetters.getTitle(articleObject.value) ||
          articleCategoryGetters.getTitle(articlesCategory.value),
      }) +
        ' | ' +
        t('general.titles.base'),
    'og:title':
      articleGetters.getSeoTitle(articleObject.value) ||
      articleCategoryGetters.getSeoTitle(articlesCategory.value) ||
      t('general.titles.magazine-slug', {
        article:
          articleGetters.getTitle(articleObject.value) ||
          articleCategoryGetters.getTitle(articlesCategory.value),
      }) +
        ' | ' +
        t('general.titles.base'),
    description: getFormattedDescription,
    'og:description': getFormattedDescription,
    ...((articleGetters.getSeoImageUrl(articleObject.value) ||
      te('general.openGraphImages.magazine-slug')) && {
      'og:image':
        articleGetters.getSeoImageUrl(articleObject.value) ||
        t('general.openGraphImages.magazine-slug'),
    }),
    'og:type': 'article',
    'og:image':
      headerImageUrl ||
      seoImageUrl ||
      t('general.openGraphImages.magazine-slug'),
  },
  head: {
    title:
      articleGetters.getSeoTitle(articleObject.value) ||
      articleCategoryGetters.getSeoTitle(articlesCategory.value) ||
      articleCategoryGetters.getTitle(articlesCategory.value) ||
      t('general.titles.magazine-slug', {
        article: articleGetters.getTitle(articleObject.value),
      }),

    ...(articleObject.value && { script: [magazineArticleSchema] }),
  },
}))

definePageMeta({
  contentGroup: 'magazine',
  preloaderType: 'magazine',
  hasCustomHreflang: true,
})

useSeoHelper(seoOptions)
</script>

<style lang="postcss" scoped>
.summary-block {
  @apply bg-[#f0f6f6] p-5 rounded-xl mb-8 text-[#1d1f22];

  &__title {
    @apply text-lg font-semibold text-black mb-5;
  }

  &__text {
    @apply max-w-none w-full;

    :deep(ul),
    :deep(ol) {
      li {
        margin-top: 0;
        padding: 0;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    :deep(p),
    :deep(ul),
    :deep(ol) {
      @apply text-[#1d1f22] mb-3 last:mb-0 mt-0;
    }
  }
}
</style>
